/**
 * Suivre une partie qu'on ne dirige pas.
 *
 * En solo, l'écran commande : il applique une action, met le jet en scène, puis passe
 * au suivant. En ligne, c'est l'inverse — **le serveur avance à son rythme**, sans
 * savoir où en sont les animations de chacun. Un client peut donc recevoir un
 * deuxième jet pendant qu'il joue le premier.
 *
 * Ce module est **pur** : il transforme « ce que le serveur dit » en « ce que l'écran
 * doit montrer », et rien d'autre. La connexion, elle, vit dans `connexion.ts`.
 *
 * ## Ce qu'il produit
 *
 * Une `Session` — exactement celle du mode solo. C'est tout l'intérêt d'avoir projeté
 * l'état plutôt qu'inventé un format réseau : `tableau.ts`, `desAffiches` et la scène
 * n'ont pas une ligne de plus à connaître du multijoueur.
 */

import {
  reconstruire, type Demande, type RuleSet, type VuePartie,
} from "@zinc421/shared";

import { jetTermine, ouvrirSession, type Session } from "../jeu/session.js";

/**
 * Nombre de jets qu'on accepte d'avoir en retard avant de rattraper.
 *
 * Au-delà, un client ralenti — onglet en arrière-plan, téléphone qui rame —
 * montrerait un lancer vieux de plusieurs secondes pendant que la partie est
 * ailleurs, et l'écart ne se refermerait jamais. Mieux vaut sauter et le dire.
 */
const RETARD_TOLERE = 2;

export interface EtatReseau {
  readonly session: Session;
  /** Jets reçus et pas encore joués, dans l'ordre du tirage. */
  readonly file: readonly Demande[];
  /** Identifiants déjà vus : l'état est repoussé à chaque patch. */
  readonly vus: ReadonlySet<number>;
  /** Vrai quand on a dû sauter des jets pour rattraper le serveur. */
  readonly enRetard: boolean;
}

/** Avant toute réception : rien à montrer, rien à jouer. */
export function suivreDepart(): EtatReseau {
  return {
    // Une partie vide en attendant le premier état ; elle est remplacée dès la
    // première réception, et n'est jamais montrée telle quelle.
    session: ouvrirSession(PARTIE_VIDE),
    file: [],
    vus: new Set<number>(),
    enRetard: false,
  };
}

/**
 * Intègre un état reçu du serveur.
 *
 * La **partie** prend toujours la dernière valeur connue : c'est le serveur qui fait
 * autorité, et un client en retard d'animation n'est pas un client en retard d'état.
 * Seuls les **jets** sont mis en file, parce qu'eux se regardent.
 */
export function recevoirEtat(
  etat: EtatReseau, vue: VuePartie, regles: RuleSet,
): EtatReseau {
  const partie = reconstruire(vue, regles);
  const demande = vue.demande;

  let file = etat.file;
  let vus = etat.vus;
  let montre = etat.session.montre;
  let enRetard = etat.enRetard;

  if (demande && !vus.has(demande.id)) {
    const suite = new Set(vus);
    suite.add(demande.id);
    vus = suite;
    file = [...file, demande];

    if (file.length > RETARD_TOLERE) {
      // On saute au dernier — mais les faces sautées sont quand même posées, sinon
      // le tapis afficherait de vieilles valeurs que l'état a déjà oubliées.
      const sautes = file.slice(0, -1);
      montre = sautes.reduce(poser, montre);
      file = file.slice(-1);
      enRetard = true;
    }
  }

  return {
    session: { partie, demande: file[0] ?? null, montre },
    file,
    vus,
    enRetard,
  };
}

/** La scène a fini de jouer le jet en tête : au suivant. */
export function jetJoue(etat: EtatReseau): EtatReseau {
  if (etat.file.length === 0) return etat;
  const apres = jetTermine(etat.session);
  const file = etat.file.slice(1);
  return {
    ...etat,
    file,
    session: { ...apres, demande: file[0] ?? null },
  };
}

/** Pose les faces d'un jet sur le tapis, sans l'animer. */
function poser(
  montre: readonly (Demande["faces"][number] | null)[], demande: Demande,
): readonly (Demande["faces"][number] | null)[] {
  const suite = [...montre];
  demande.indices.forEach((index, rang) => { suite[index] = demande.faces[rang]!; });
  return suite;
}

/** Partie neutre, remplacée dès le premier état reçu. */
const PARTIE_VIDE = reconstruire({
  revision: 0,
  ruleSetId: "",
  phase: "CHARGE",
  masse: 0,
  joueurs: [],
  actifs: [],
  classement: [],
  manche: null,
  perdant: -1,
  demande: null,
}, { id: "", masse: 0, secDouble: true });
